import React, { useEffect, useState } from 'react';
import './ProjectDetails.css';
import { useParams } from 'react-router-dom';
import ProjectTemplate from '../components/ProjectTemplate';

const placeholderProjects = {
  construction: {
    id: 'construction',
    title: 'SiJu Construct',
    hero: '/project_cons/siju_cons.webp',
    summary: 'SiJu Construct is a complete business management platform for construction companies—streamlining planning, execution, and collaboration from the office to the job site.',
    availableIn: ['desktop', 'web', 'android'],

    details: `SiJu Construct helps construction businesses stay in control of their projects. 
From scheduling and material tracking to site reporting and finances, everything is managed in one simple platform. 

It reduces delays, prevents overspending, and keeps teams accountable—leading to smoother projects and happier clients. 

Built for today’s construction challenges, SiJu Construct is easy to use and fits naturally into daily work for owners, supervisors, and managers alike.`,
    demoUrl: 'https://app.alviconstruction.top',
    features: [
      {
        id: 'f1',
        title: 'Business Dashboard',
        img: '/project_cons/1.png',
        desc: 'Get a clear overview of your business in one place. Track active projects, workers, daily expenses, and payments instantly.',
      },
      {
        id: 'f2',
        title: 'Invoices',
        img: '/project_cons/2.png',
        desc: 'Easily manage purchase invoices. Record items, payments, and keep everything organized for smooth procurement.',
      },
      {
        id: 'f3',
        title: 'Project Expense Report',
        img: '/project_cons/3.png',
        desc: 'See every expense of a project in detail. Stay on top of costs and avoid budget surprises.',
      },
      {
        id: 'f4',
        title: 'Worker Attendance',
        img: '/project_cons/4.png',
        desc: 'Record attendance project by project. Simple input makes daily worker tracking fast and accurate.',
      },
      {
        id: 'f5',
        title: 'Worker Payments',
        img: '/project_cons/5.png',
        desc: 'Check worker attendance and calculate payments automatically. No more manual paperwork.',
      },
      {
        id: 'f6',
        title: 'Report Printing',
        img: '/project_cons/6.png',
        desc: 'Generate ready-to-share PDF reports with project data, expenses, and summaries for clients or management.',
      },
      {
        id: 'f7',
        title: 'Mobile & Foreman App',
        img: '/project_cons/7.png',
        desc: 'Use the same system on mobile. Foremen can update site info directly from the field for real-time reporting.',
      },
    ],
  },
  'b2b-shop-management': {
    id: 'b2b-shop-management',
    title: 'B2B Shop Management',
    hero: '/projects/b2b.png',
    summary: 'An offline-first, multi-branch B2B management platform that covers sales, inventory, invoicing and client ledgers.',
    availableIn: ['android', 'web'],
    details: `B2B Shop Management is an offline-first solution for wholesale distributors and field sales teams — fast order capture, per-client pricing, multi-branch stock, and compact invoicing.

Built as a Flutter mobile app with Hive for local storage, it syncs to an Express + MySQL backend when online. Admins get sales dashboards, stock visibility across branches, and exportable invoices/ledgers for accounting. Easily integrates with existing ERP systems (e.g. Ultimate POS, Odoo) via APIs.`,
    demoUrl: '#',
    features: [
      {
        id: 'b1',
        title: 'Offline-First Order Capture',
        img: '/b2b/order.webp',
        desc: 'Field agents can create leads, take orders, and collect signatures while offline; orders sync automatically when online.',
      },
      {
        id: 'b2',
        title: 'Multi-branch Inventory',
        img: '/b2b/inventory.webp',
        desc: 'Real-time stock visibility across warehouses and shops with transfer requests and low-stock alerts.',
      },
      {
        id: 'b3',
        title: 'Client Pricing & Credit',
        img: '/b2b/price.webp',
        desc: 'Per-client pricing tiers, negotiated discounts, and credit limits with aging reports for receivables.',
      },
      {
        id: 'b4',
        title: 'Client Management',
        img: '/b2b/client.webp',
        desc: 'Centralized client profiles with contact info, credit limits, payment history, and custom pricing tiers.',
      },
      {
        id: 'b5',
        title: 'Order Management & History',
        img: '/b2b/orders.webp',
        desc: 'Complete order lifecycle: draft, confirm, deliver, and maintain searchable order history with timestamps and status updates.',
      },
      {
        id: 'b6',
        title: 'Map & Routing',
        img: '/b2b/map.webp',
        desc: 'Integrated maps for client locations, route planning for field agents, estimated travel time and distance, and optimized visit sequencing to reduce travel time and costs.',
      },
      {
        id: 'b7',
        title: 'Invoicing',
        img: '/b2b/invoice.webp',
        desc: 'Create, preview, print and send PDF invoices; manage invoice numbering, collections and downloadable ledgers for accounting.',
      },
    ],
  },
  vocabulary: {
    id: 'vocabulary',
    title: 'Vocabulary Builder',
    hero: '/projects/vocab.png',
    summary: 'An interactive platform for students to enhance their vocabulary skills. Modern design with gamified learning.',
    availableIn: ['web', 'android'],
    details: `Placeholder details for Vocabulary Builder. Include learning modes, progress tracking, spaced repetition, and sample screens here.`,
    demoUrl: '#',
    features: [
      {
        id: 'v1',
        title: 'Spaced Repetition',
        img: '/projects/vocab.png',
        desc: 'Helps learners retain words using spaced repetition algorithms.',
      },
    ],
  },
  'school-website': {
    id: 'school-website',
    title: 'School Website',
    hero: '/school/sc9.png',
    summary: 'A responsive, content-first website for schools with CMS, events, admissions, and parent communication.',
    availableIn: ['web'],
    details: `A sleek, easy-to-manage school website built to showcase academics, streamline admissions, and keep parents informed. Features a lightweight CMS for updating news and pages, an events calendar with registration, staff and department directories, and a secure parent portal for announcements and student records. Optimized for accessibility and search engines to help schools reach families online.`,
    demoUrl: '#',
    features: [
      {
        id: 's1',
        title: 'Homepage & Highlights',
        img: '/school/sc9.png',
        desc: 'Engaging homepage with school logo, mission statement, principal’s message, and quick links to admissions, events, and parent portal.'
      },
      {
        id: 's2',
        title: 'About the School',
        img: '/school/sc10.png',
        desc: 'Dedicated section for history, vision, leadership, awards, and campus facilities with photos, videos, or virtual tours.'
      },
      {
        id: 's3',
        title: 'Admissions Made Simple',
        img: '/school/sc3.png',
        desc: 'Step-by-step admissions process with online applications, fee details, scholarships, and parent FAQs.'
      },
      {
        id: 's4',
        title: 'Academics & Curriculum',
        img: '/school/sc1.png',
        desc: 'Comprehensive overview of programs, subjects, teaching methods, schedules, and the academic calendar.'
      },
      {
        id: 's5',
        title: 'Student Life & Activities',
        img: '/school/sc11.png',
        desc: 'Showcase extracurricular activities, events, clubs, student leadership, and community initiatives that enrich learning.'
      },
      {
        id: 's6',
        title: 'Events & Calendar',
        img: '/school/sc5.png',
        desc: 'Interactive calendar with upcoming events, holidays, and parent-teacher meetings that can be registered or exported.'
      },
      {
        id: 's7',
        title: 'Parent Resources & Portal',
        img: '/school/sc4.png',
        desc: 'Secure portal for grades, attendance, announcements, and fee payments, plus guidelines and handbooks for families.'
      },
      {
        id: 's8',
        title: 'Faculty & Staff Directory',
        img: '/school/sc6.png',
        desc: 'Detailed directory of teachers and staff with photos, qualifications, subjects taught, and contact information.'
      },
      {
        id: 's9',
        title: 'News & Announcements',
        img: '/school/sc8.png',
        desc: 'Easy-to-update section for school news, achievements, exam notices, and newsletters to keep families informed.'
      },
      {
        id: 's10',
        title: 'Contact & Location',
        img: '/school/sc7.png',
        desc: 'Clear contact information with forms, office hours, and an interactive map to help families reach the campus easily.'
      },
    ],
  },
  tbc: {
    id: 'tbc',
    title: 'TBC Help Line',
    hero: '/projects/tbc.png',
    summary: 'Android app for TBC, a volunteer social workers group, used by internal members and external users for help applications, member coordination, and fundraising.',
    availableIn: ['android'],
    details: `TBC Help Line is an Android-first app for both external applicants and internal volunteers. Anyone can submit a help application via the app; internal admins and volunteers triage, assign, and follow up on cases. Applicants can track the status of their requests. Organizers can also run fundraising campaigns, publish impact stories, and coordinate volunteers. The app focuses on quick intake, transparent status updates, and simple field tools for volunteers.`,
    demoUrl: '#',
    features: [
      { id: 't1', title: 'Member Management', img: '/tbc/member.webp', desc: 'Register and manage volunteers with roles, contact details, and availability.' },
      { id: 't2', title: 'Help Application Intake', img: '/tbc/application.webp', desc: 'External users can submit help requests with photos, location, and urgency; applicants receive status updates.' },
      { id: 't3', title: 'Fundraising & Donations', img: '/projects/tbc.png', desc: 'Create campaigns, accept donations, and track supporters with simple reporting.' },
      { id: 't4', title: 'Case Assignment & Follow-up', img: '/projects/tbc.png', desc: 'Internal admins assign volunteers, track status, add case notes, and close cases; applicants can view progress.' },
      { id: 't5', title: 'Activity Feed & Reports', img: '/projects/tbc.png', desc: 'Share success stories, shift schedules, and donation summaries to keep the community informed.' },
    ],
  },
  'family-budget': {
    id: 'family-budget',
    title: 'Family Budget & Expense Tracker',
    hero: '/projects/expense.png',
    summary: 'A privacy-first personal and household expense tracker — fast, reliable and fully local for confident family use.',
    availableIn: ['android', 'web'],
    details: `Family Budget is a privacy-first, single-device expense and budget tracker designed for speed and reliability. Because it stores data locally, the app works instantly and offline without accounts or cloud setup — great for families and individuals who prefer simple, private tools. Backups are easy: export/import a file to move data between devices or keep offline copies. The result is a lightweight, trustworthy budgeting tool that puts control in the user’s hands.`,
    demoUrl: 'https://giz.nextgentechies.sbs/',
    features: [
      {
        id: 'fb1',
        title: 'Dashboard & Expense Entry',
        img: '/exp/exp1.webp',
        desc: 'View the current month’s spending and budget progress with circular and linear bars, see recent expenses, and quickly add new expenses with a floating button. Add expenses by category and sub-category with title, amount, optional remarks, and date selection.',
      },
      {
        id: 'fb2',
        title: 'Budget Allocation & Category Management',
        img: '/exp/cat.webp',
        desc: 'Manage categories and sub-categories, assign budgets for each category, and track how much has been spent. Helps families plan finances and control spending effectively.',
      },
      {
        id: 'fb3',
        title: 'Summary & Insights',
        img: '/exp/summary.webp',
        desc: 'View monthly and category-wise budget summaries, track spent amounts, drill down to sub-category totals, and review monthly expenses over time for better financial awareness.',
      },
      {
        id: 'fb4',
        title: 'Expense History & Search',
        img: '/exp/history.webp',
        desc: 'Browse all expenses sorted by amount, date, category, or sub-category. Search by title or remarks and filter by date ranges for complete control over records.',
      },
      {
        id: 'fb5',
        title: 'Private, Local Storage',
        img: '/exp/privacy.webp',
        desc: 'All data stays locally on the device for privacy and speed. No cloud accounts or sign-ups required, keeping family financial data secure and fully under user control.',
      },
    ],
  },
};

export default function ProjectDetails() {
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem('siju-theme');
      if (stored === 'light' || stored === 'dark') return stored;
      const prefersLight = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches;
      return prefersLight ? 'light' : 'dark';
    } catch (e) {
      return 'dark';
    }
  });
  // eslint-disable-next-line no-unused-vars
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('siju-theme', theme); } catch (e) { }
  }, [theme]);

  // eslint-disable-next-line no-unused-vars
  function toggleTheme() { setTheme((t) => (t === 'dark' ? 'light' : 'dark')); }

  const { id } = useParams();
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      // ignore in non-browser environments
    }
  }, [id]);
  const project = placeholderProjects[id] || { title: 'Project not found', summary: '', details: '' };

  return <ProjectTemplate project={project} contactHref="#contact" />;
}
